import { useState } from "react";
import { ArrowRight, ChevronDown, ChevronUp } from "lucide-react";
import { useAdRotation, AdLink, AdMedia } from "@/components/AdSlot";

const COLLAPSE_KEY = "nl-sponsor-banner-collapsed";

const Chip = ({ children }: { children: string }) => (
  <span className="font-mono text-[9px] uppercase tracking-widest text-[#7a8299] border border-[#1c212e] rounded px-1.5 py-0.5 shrink-0">
    {children}
  </span>
);

const BannerArt = ({ accent }: { accent: string }) => (
  <div aria-hidden className="absolute inset-0 pointer-events-none">
    <div className="absolute inset-0"
      style={{ background: `radial-gradient(ellipse 55% 150% at 84% 50%, ${accent}30, transparent 68%)` }} />
    <div className="absolute inset-0"
      style={{ background: `radial-gradient(ellipse 40% 130% at 6% 115%, ${accent}16, transparent 70%)` }} />
    <div className="absolute inset-y-0 right-0 w-[44%]"
      style={{
        backgroundImage: "radial-gradient(rgba(255,255,255,0.13) 1px, transparent 1.4px)",
        backgroundSize: "17px 17px",
        WebkitMaskImage: "linear-gradient(to left, rgba(0,0,0,0.85), transparent 90%)",
        maskImage: "linear-gradient(to left, rgba(0,0,0,0.85), transparent 90%)",
      }} />
    <svg viewBox="0 0 900 96" preserveAspectRatio="none" className="absolute inset-0 h-full w-full opacity-80">
      <defs>
        <linearGradient id="sb-wave" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor={accent} stopOpacity="0" />
          <stop offset="55%" stopColor={accent} stopOpacity="0.14" />
          <stop offset="100%" stopColor={accent} stopOpacity="0.5" />
        </linearGradient>
      </defs>
      <path d="M0 84 C 260 70 460 92 900 42" fill="none" stroke="url(#sb-wave)" strokeWidth="1" />
      <path d="M0 64 C 300 52 520 80 900 22" fill="none" stroke="url(#sb-wave)" strokeWidth="1" />
      <path d="M0 44 C 340 36 560 66 900 6" fill="none" stroke="url(#sb-wave)" strokeWidth="1" />
      <path d="M120 96 C 420 84 640 96 900 62" fill="none" stroke="url(#sb-wave)" strokeWidth="0.7" />
    </svg>
    <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-white/[0.18] to-transparent" />
  </div>
);

const CollapseBtn = ({ collapsed, onToggle, className = "" }: { collapsed: boolean; onToggle: () => void; className?: string }) => (
  <button type="button" onClick={onToggle} data-testid="sponsor-banner-toggle"
    aria-expanded={!collapsed} aria-label={collapsed ? "Expand sponsor banner" : "Collapse sponsor banner"}
    className={`inline-flex items-center gap-1 rounded border border-[#1c212e] text-[#7a8299] hover:text-white hover:border-white/35 transition-colors font-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 shrink-0 ${className}`}>
    {collapsed ? <ChevronDown className="h-3 w-3" /> : <ChevronUp className="h-3 w-3" />}
    <span className="hidden sm:inline">{collapsed ? "Show" : "Hide"}</span>
  </button>
);

export const SponsorBanner = () => {
  const { ad, hidden } = useAdRotation("homepage_banner");
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(COLLAPSE_KEY) === "1"; } catch { return false; }
  });
  if (hidden || !ad) return null;
  const b = ad.banner;
  const chip = ad.is_house ? "Promo" : "Sponsored";
  const toggle = () => setCollapsed((c) => {
    try { localStorage.setItem(COLLAPSE_KEY, c ? "0" : "1"); } catch { /* private mode */ }
    return !c;
  });

  if (collapsed) {
    return (
      <div data-testid="sponsor-banner" className="mb-4 rounded-xl bg-[#000000] flex items-center justify-between gap-3 px-4 sm:px-5 py-2">
        <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#7a8299] truncate">
          {chip} · {ad.advertiser}
        </span>
        <span className="flex items-center gap-2 shrink-0">
          <Chip>Ad</Chip>
          <CollapseBtn collapsed onToggle={toggle} />
        </span>
      </div>
    );
  }

  if (!b) {
    return (
      <div data-testid="sponsor-banner" className="mb-4 rounded-xl bg-[#0a0a12] overflow-hidden">
        <div className="flex items-center justify-between px-4 sm:px-5 py-2 border-b border-white/[0.06]">
          <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#7a8299]">
            Sponsored · {ad.advertiser}
          </span>
          <span className="flex items-center gap-2">
            <Chip>Ad</Chip>
            <CollapseBtn collapsed={false} onToggle={toggle} />
          </span>
        </div>
        <AdLink ad={ad} testid="sponsor-banner-link" className="group block">
          <AdMedia ad={ad} eager
            className="w-full h-auto max-h-[180px] object-cover transition-transform duration-500 group-hover:scale-[1.01]" />
        </AdLink>
      </div>
    );
  }

  const accent = b.accent || "#7C3AED";
  return (
    <div data-testid="sponsor-banner" className="mb-4 relative">
      <AdLink ad={ad} testid="sponsor-banner-link"
        className="group relative block rounded-xl bg-[#08080f] overflow-hidden">
        <BannerArt accent={accent} />
        <div className="relative flex flex-col lg:flex-row lg:items-center gap-3 lg:gap-8 pl-5 sm:pl-8 pr-10 sm:pr-14 py-4">
          <span className="hidden lg:block font-mono text-[9px] uppercase tracking-[0.3em] text-gray-400 shrink-0">
            {b.label}
          </span>
          <span aria-hidden className="hidden lg:block h-9 w-px bg-white/[0.08] shrink-0" />
          <span className="flex items-center justify-between lg:justify-start gap-3 min-w-0 lg:shrink-0">
            <span className="flex items-center gap-3 min-w-0">
              <span aria-hidden className="h-10 w-10 rounded-xl border flex items-center justify-center font-heading font-black text-base shrink-0"
                style={{
                  borderColor: `${accent}77`, color: accent,
                  background: `linear-gradient(135deg, ${accent}33, ${accent}0d)`,
                  boxShadow: `0 0 22px ${accent}40`,
                }}>
                {String(b.brand || "N").charAt(0)}
              </span>
              <span className="font-heading font-black text-white text-lg lg:text-xl leading-tight truncate lg:whitespace-nowrap">{b.brand}</span>
            </span>
            <span className="lg:hidden"><Chip>{chip}</Chip></span>
          </span>
          <span className="min-w-0 flex-1 lg:pl-2">
            <span className="block font-heading font-black text-white text-[16px] sm:text-lg xl:text-xl leading-snug">{b.headline}</span>
            {b.subline && (
              <span className="block text-gray-400 text-[13px] mt-0.5 line-clamp-2 lg:line-clamp-1">{b.subline}</span>
            )}
          </span>
          <span className="shrink-0 flex items-center gap-3">
            <span className="inline-flex items-center gap-2 rounded-lg border text-white font-semibold text-sm px-4 py-2 transition-colors whitespace-nowrap"
              style={{ borderColor: `${accent}88`, background: `${accent}14` }}>
              {b.cta}
              <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
            </span>
            <span className="hidden lg:inline-flex"><Chip>{chip}</Chip></span>
          </span>
        </div>
      </AdLink>
      <CollapseBtn collapsed={false} onToggle={toggle} className="absolute top-2 right-2 z-10 bg-black/60 backdrop-blur-sm" />
    </div>
  );
};
